import { useState, useEffect } from 'preact/hooks';
import type { Connection } from '../../../types';
import { connectionManagementService } from '../../../services/connections/connection-management.service';
import { connectorRegistryService } from '../../../services/connectors/connector-registry.service';
import { credentialSchemaService } from '../services/credential-schema.service';
import { useScopeContext } from '../../../contexts/ScopeContext';
import { toast } from '../../../services/toast.service';

interface CredentialsTabProps {
  connection: Connection;
  onConnectionUpdate: () => void;
}

export function CredentialsTab({ connection, onConnectionUpdate }: CredentialsTabProps) {
  const scope = useScopeContext();
  const [requiredCredentials, setRequiredCredentials] = useState<any>(null);
  const [credentials, setCredentials] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCredentialSchema();
  }, [connection.connectionId || connection.id]);

  useEffect(() => {
    // Initialize credentials with default values when requiredCredentials is loaded
    if (requiredCredentials?.properties && !connection?.hasCredentials) {
      const defaults: Record<string, any> = {};
      for (const [key, config] of Object.entries(requiredCredentials.properties)) {
        if ((config as any).default !== undefined) {
          defaults[key] = (config as any).default;
        }
      }
      setCredentials(defaults);
    }
  }, [requiredCredentials, connection?.hasCredentials]);

  const loadCredentialSchema = async () => {
    try {
      setLoading(true);
      setError(null);

      const connectorId = connection.connectorId!;
      const connectorInfo = await connectorRegistryService.getConnector(scope.teamId, connectorId);

      if (connectorInfo?.requiredCredentials) {
        setRequiredCredentials(connectorInfo.requiredCredentials);
      } else {
        setError('No credential schema found for this connector');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credential configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name: string, value: any) => {
    setCredentials(prev => ({ ...prev, [name]: value }));
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSaveCredentials = async (e: Event) => {
    e.preventDefault();
    setValidationErrors([]);

    // Validate credentials against schema
    if (requiredCredentials) {
      const validation = credentialSchemaService.validateCredentials(
        requiredCredentials,
        credentials
      );

      if (!validation.valid && validation.errors) {
        setValidationErrors(validation.errors);
        return;
      }
    }

    setSaving(true);
    try {
      await connectionManagementService.updateCredentials(scope.teamId, connection.connectionId || connection.id, credentials);
      await onConnectionUpdate();
      setValidationErrors([]);
      setShowCredentialForm(false);
      setCredentials({});
      toast.success('Credentials saved successfully');
    } catch (err) {
      toast.error(`Failed to save credentials: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = connection?.hasCredentials === true;

  if (isConfigured) {
    return (
      <div className="space-y-4">
        <div className="alert alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Credentials are configured for {connection?.connectorId}</span>
        </div>

        {!showCredentialForm ? (
          <div className="card bg-base-100">
            <div className="card-body">
              <h3 className="card-title">Credential Management</h3>
              <p className="text-sm opacity-70">Your credentials are securely stored and encrypted. You can update them or test the connection.</p>
              <div className="card-actions justify-end mt-4">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    // Initialize credentials with default values from schema
                    if (requiredCredentials?.properties) {
                      const defaults: Record<string, any> = {};
                      for (const [key, config] of Object.entries(requiredCredentials.properties)) {
                        if ((config as any).default !== undefined) {
                          defaults[key] = (config as any).default;
                        }
                      }
                      setCredentials(defaults);
                    }
                    setShowCredentialForm(true);
                  }}
                >
                  Update Credentials
                </button>
              </div>
            </div>
          </div>
        ) : (
          <CredentialForm
            requiredCredentials={requiredCredentials}
            credentials={credentials}
            validationErrors={validationErrors}
            saving={saving}
            onFieldChange={handleFieldChange}
            onSubmit={handleSaveCredentials}
            onCancel={() => {
              setShowCredentialForm(false);
              setCredentials({});
              setValidationErrors([]);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="alert alert-info">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>Configure credentials for {connection?.connectorId}</span>
      </div>

      {loading ? (
        <div className="alert alert-warning">
          <span className="loading loading-spinner"></span>
          <span>Loading credential configuration...</span>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      ) : requiredCredentials ? (
        <CredentialForm
          requiredCredentials={requiredCredentials}
          credentials={credentials}
          validationErrors={validationErrors}
          saving={saving}
          onFieldChange={handleFieldChange}
          onSubmit={handleSaveCredentials}
        />
      ) : (
        <div className="alert">
          <span>No credential configuration required for this connection</span>
        </div>
      )}
    </div>
  );
}

interface CredentialFormProps {
  requiredCredentials: any;
  credentials: Record<string, any>;
  validationErrors: string[];
  saving: boolean;
  onFieldChange: (name: string, value: any) => void;
  onSubmit: (e: Event) => void;
  onCancel?: () => void;
}

function CredentialForm({
  requiredCredentials,
  credentials,
  validationErrors,
  saving,
  onFieldChange,
  onSubmit,
  onCancel
}: CredentialFormProps) {
  return (
    <form onSubmit={onSubmit}>
      {onCancel && (
        <div className="alert alert-warning mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span className="text-sm">For security reasons, existing credential values are not displayed. Enter new values to update the credentials.</span>
        </div>
      )}

      {credentialSchemaService.renderSchemaFields(
        requiredCredentials,
        credentials,
        onFieldChange
      )}

      {validationErrors.length > 0 && (
        <div className="alert alert-error mt-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-bold">Please fix the following errors:</h3>
            <ul className="list-disc list-inside">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="card-actions justify-end mt-4">
        {onCancel && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (onCancel ? 'Updating...' : 'Saving...') : (onCancel ? 'Update Credentials' : 'Save Credentials')}
        </button>
      </div>
    </form>
  );
}
